<template>
    <div
        class="WFile"
        :class="{ error }"
    >
        <input
            :id="id"
            :value="value"
            :name="name"
            type="file"
            :readonly="readonly"
            :disabled="disabled"
            :multiple="multiple"
            :accept="accept"
            v-on="inputListeners"
        >
        <div
            class="File flex col-center"
            :class="{ filled, dragging }"
        >
            <WIcon
                v-if="icon"
                class="icon"
                :icon="!filled ? icon : 'check-circle'"
                :color="!filled ? iconColor : 'primary'"
                :size="34"
            />
            <WText
                :for="id"
                tag="label"
                :size="14"
                ellipsis
                class="mb-0"
            >
                <slot
                    name="label"
                    v-bind="{ file }"
                >
                    {{ file }}
                </slot>
            </WText>
        </div>

        <Transition
            name="fade"
            :duration="500"
        >
            <WText
                v-if="error"
                class="Error"
                typo="span"
                :size="12"
                color="red"
            >
                {{ error }}
            </WText>
        </Transition>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { merge } from 'lodash'
import { generateIdMixin } from '@/mixins'

export default {
    name: 'WFile',
    mixins: [generateIdMixin],
    props: {
        multiple: VueTypes.bool.def(false),
        name: VueTypes.string.isRequired,
        error: VueTypes.string.def(''),
        placeholder: VueTypes.string.def(''),
        readonly: VueTypes.bool.def(false),
        disabled: VueTypes.bool.def(false),
        value: VueTypes.oneOfType([String, Number]),
        accept: VueTypes.string, // Accept HTML Attr file types (.csv)
        icon: VueTypes.string.def('upload-cloud'),
        iconColor: VueTypes.oneOf([
            'white',
            'primary',
            'secondary',
            'dark',
            'grayMedium',
            'black',
            'yellow',
        ]),
    },
    data () {
        return {
            file: '',

            filled: false,
            dragging: false,
        }
    },
    computed: {
        inputListeners () {
            return merge({}, this.$listeners, {
                input: (event) => {
                    const { files } = event.target
                    if ((files || []).length) this.handleFile(files)
                    this.$emit('input', files, this.id)
                },
                ...['dragover', 'dragenter'].reduce((acc, key) => ({ ...acc, [key]: this.enableDrag }), {}),
                ...['dragleave', 'dragend', 'drop'].reduce((acc, key) => ({ ...acc, [key]: this.disableDrag }), {}),
            })
        },
    },
    mounted () {
        this.file = this.placeholder
    },
    methods: {
        enableDrag () {
            this.dragging = true
        },
        disableDrag () {
            this.dragging = true
        },
        handleFile (files) {
            this.file = files[0].name
            this.filled = true
        },
    },
}
</script>

<style scoped lang="scss">
@import "@/theme/mixins/_global.scss";

$file-height: 135px;

input {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    opacity: 0;
    cursor: pointer;

    &:focus + .File {
        cursor: pointer;
        outline: none;
    }

    &:hover + .File {
        background-color: $form-dropdown-bg;
    }

    @include placeholder {
        color: $form-placeholder-color;
    }
    &:focus {
        border-color: $form-border-color-active;
        outline: none;
    }
    &[readonly] {
        opacity: 0.5;
        cursor: not-allowed;
	}
	&:disabled {
        cursor: not-allowed;
        background-color: $gray-light;
    }
    &.right {
        text-align: right;
    }
    &.center {
        text-align: center;
    }
    &.left {
        text-align: left;
    }

}

.File {
    width: calc(100% - 10px);
    min-height: $file-height;
    padding: 0 5px;
    color: $gray-01;
    background-color: transparent;
    border: $form-border;
    border-style: dashed;
    border-radius: 4px;
    line-height: 2;
    outline: none;
    font-size: $form-font-size;
    font-weight: $form-font-weight;
    transition: all 350ms ease-in-out;
    cursor: pointer;

    &.filled, &.dragging {
        background-color: $gray-06;
    }

    .WText {
        max-width: 90%;
        margin-top: 10px;
    }
}

.WFile {
    position: relative;

    &.error {
        input {
        border-color: $danger;
        }
    }
}

.Error {
    position: absolute;
    top: $file-height + 5px;
    left: 5px;
}
</style>
